import { Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { AuthService } from '../../services/auth.service';
import { Gateway } from '../../interfaces/Gateway';

export type GatewayToolMode = 'username' | 'password' | 'replace';

/**
 * Shared admin modal for the gateway Tools actions that need input:
 * Change username, Change password, and Replace. `mode` selects the fields.
 */
@Component({
    selector: 'app-gateway-tool-modal',
    imports: [FormsModule, NgSelectModule],
    templateUrl: './gateway-tool-modal.component.html',
    styleUrl: './gateway-tool-modal.component.scss',
})
export class GatewayToolModalComponent implements OnInit {
    @Input() mode: GatewayToolMode = 'username';
    @Input() gateway!: Gateway;
    /** Full list, used to pick a replacement in `replace` mode. */
    @Input() gateways: Gateway[] = [];

    newUsername = '';
    newPassword = '';
    confirmPassword = '';
    replacementUid: string | null = null;
    submitting = false;
    error: string | null = null;

    /** Stable ng-select options (computed once — a getter would return a new
     *  array each change-detection pass and break selection). */
    candidateOptions: { uid: string; label: string }[] = [];

    constructor(
        public activeModal: NgbActiveModal,
        private authService: AuthService,
    ) { }

    ngOnInit(): void {
        this.candidateOptions = this.candidates.map((c) => ({
            uid: c.uid,
            label: `${c.uid} — ${c.customerName || c.descr || ''}`.trim(),
        }));
    }

    get title(): string {
        return this.mode === 'username' ? 'Change username'
            : this.mode === 'password' ? 'Change password'
                : 'Replace gateway';
    }

    /**
     * The other gateways on this customer's account.
     *
     * A username is the cloud LOGIN, so renaming it affects every gateway the
     * account owns — not the row the admin happened to click. 14 of the 123
     * owning accounts hold more than one gateway (up to five), and in all 14
     * the per-row customer names differ, so the clicked row's name is actively
     * misleading. The old site showed only that name and said nothing.
     */
    get siblingGateways(): Gateway[] {
        const owner = this.gateway?.cloudUserId;
        if (!owner) return [];
        // Compared by identity, not uid: three uids in the data are shared by
        // two gateways each, so excluding "same uid" would make such a pair
        // hide each other and show no warning at all.
        return this.gateways.filter((g) => g.cloudUserId === owner && g !== this.gateway);
    }

    /** How the affected gateways read in the warning. */
    get siblingLabels(): string {
        return this.siblingGateways
            .map((g) => g.customerName || g.description || g.code || g.uid)
            .join(', ');
    }

    /**
     * Candidate replacement gateways: everything except this one.
     *
     * INTENTIONALLY UNFILTERED — the old site's `$select_gateways` listed every
     * gateway post, and that behaviour is being kept deliberately.
     *
     * Be aware of what it allows: 173 of the 284 selectable gateways are already
     * claimed, and picking one of those as the replacement transfers this
     * customer onto it — overwriting that gateway's own customerName,
     * cloudUserId and activationDate, and swapping its code away. Two customers,
     * one record, no warning. Restricting the list to unclaimed gateways, or
     * refusing a claimed target in POST /gateways/replace, would close it; that
     * is a product decision, not a technical one.
     */
    get candidates(): Gateway[] {
        return this.gateways.filter((g) => g.uid !== this.gateway?.uid);
    }

    submit(): void {
        if (this.submitting) return;
        this.error = null;

        let request$;
        if (this.mode === 'username') {
            if (!this.newUsername.trim()) return;
            request$ = this.authService.changeUsername(this.gateway.cloudUserId!, this.newUsername.trim());
        } else if (this.mode === 'password') {
            if (!this.newPassword) return;
            if (this.newPassword !== this.confirmPassword) {
                this.error = 'Passwords do not match';
                return;
            }
            request$ = this.authService.changePassword(this.gateway.cloudUserId!, this.newPassword);
        } else {
            const cand = this.candidates.find((c) => c.uid === this.replacementUid);
            if (!cand || !cand.code) {
                this.error = 'Please select a replacement gateway';
                return;
            }
            request$ = this.authService.replaceGateway(this.gateway.uid, cand.uid, cand.code);
        }

        this.submitting = true;
        request$.subscribe({
            next: () => {
                this.submitting = false;
                this.activeModal.close(true);
            },
            error: (err) => {
                this.submitting = false;
                // Show what the backend actually said. It distinguishes the
                // cases — "That username is already in use by another account",
                // "More than one account is linked to this cloud user" — and a
                // catch-all string would have hidden every one of them.
                this.error = err?.error?.message
                    || err?.message
                    || 'Something went wrong. Please try again.';
            },
        });
    }
}
